import { useMemo } from "react";

import { useConnection } from "@/components/features/items/edit/_shared/ui/components/main/questions/connection/hooks";
import { useSingleConnection } from "@/components/features/items/edit/_shared/ui/components/main/questions/connection/hooks/use-single-connection";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import { useProductDayTripPlanFormModelContext } from "../../../../context";
import { usePlanRoomStaticPrice } from "../../../price-and-policy/room-static-price/hooks";

import type { CheckedState } from "@radix-ui/react-checkbox";

export const usePlanAvailableRoomIDs = () => {
	const { viewModel, onSetViewModel, queryData } = useProductDayTripPlanFormModelContext();
	const { availableRoomIDs, isShowRoomForGuest } = viewModel;
	const { roomNameAndIDOptions } = queryData.connection;

	const { inUse, initializeStaticPriceParams } = usePlanRoomStaticPrice();

	const _multipleConnection = useConnection({
		availableItemIDs: availableRoomIDs, //
		nameAndIDOptions: roomNameAndIDOptions,
		keyOfValue: "availableRoomIDs",
		onSetViewModel,
	});

	const _singleConnection = useSingleConnection({
		availableItemIDs: availableRoomIDs, //
		nameAndIDOptions: roomNameAndIDOptions,
		keyOfValue: "availableRoomIDs",
		onSetViewModel,
	});

	const multipleConnection = useMemo(() => {
		return {
			isShowRoomForGuest,
			..._multipleConnection,
			onSetAvailableItemIDs: (_checkedState: CheckedState, _item: FormOptionType) => {
				const newAvailableItemIDs = _multipleConnection.onSetAvailableItemIDs(_checkedState, _item);

				if (inUse) {
					// 部屋に定額料金を設定している場合は、紐付けている部屋を変更した場合初期化する
					initializeStaticPriceParams(newAvailableItemIDs.value.map((item) => item.value));
				}
			},
		};
	}, [_multipleConnection, inUse, initializeStaticPriceParams, isShowRoomForGuest]);

	const singleConnection = useMemo(() => {
		return {
			isShowRoomForGuest,
			..._singleConnection,
			onSetAvailableItemIDs: (_checkedState: CheckedState, _item: FormOptionType) => {
				const newAvailableItemIDs = _singleConnection.onSetAvailableItemIDs(_checkedState, _item);

				if (inUse) {
					// 部屋に定額料金を設定している場合は、紐付けている部屋を変更した場合初期化する
					initializeStaticPriceParams(newAvailableItemIDs.value.map((item) => item.value));
				}
			},
		};
	}, [_singleConnection, inUse, initializeStaticPriceParams, isShowRoomForGuest]);

	return isShowRoomForGuest.value ? multipleConnection : singleConnection;
};
